import RestEndpointsEnum from "../models/RestEndpointsEnum";
import { buildRestUrl, buildAuthHeadersForFetch } from "./RestService";
import IUserUI from "../models/IUserUI";
import ISupplierModel from "../models/ISupplierModel";
import IVendorModel from "../models/IVendorModel";
import ICart from "../models/ICart";
import IOnGetInactiveCartsDTO from "../models/DTO/IOnGetInactiveCartsDTO";
import IOnGetCartStatusDTO from "../models/DTO/IOnGetCartStatusDTO";
import IOnCreateCartPaymentIntentDTO from "../models/DTO/IOnCreateCartPaymentIntentDTO";
import IOnConfirmCartPaymentDTO from "../models/DTO/IOnConfirmCartPaymentDTO";
import IOnCancelCartDTO from "../models/DTO/IOnCancelCartDTO";
import IOnCopyCartItemsDTO from "../models/DTO/IOnCopyCartItemsDTO";
import ICopyCartItemsRestDTO from "../models/DTO/ICopyCartItemsRestDTO";

interface CreateCartFromOrderOptions {
    orderGuid: string;
    clearExistingItems?: boolean;
    skipUnavailableItems?: boolean;
}

interface CreateCartFromOrderResponse {
    cart: ICart;
}

interface GetCartResponse {
    cart: ICart;
}

interface GetCartByGuidResponse {
    cart: ICart;
}

/**
 * Create a cart from an existing order (reorder functionality)
 * Copies items from the specified order into the user's cart
 */
const createCartFromOrder = async (
    options: CreateCartFromOrderOptions,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<CreateCartFromOrderResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartCreateFromOrder,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(options),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Get the current cart for the user
 */
const getCart = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    signal?: AbortSignal
): Promise<GetCartResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartGetCart,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Get the user's inactive carts (PENDING / PAYMENT_PENDING / PAYMENT_ERRORED / FAILED).
 */
const getInactiveCarts = async (
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    signal?: AbortSignal
): Promise<IOnGetInactiveCartsDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartGetInactive,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Fetch a specific cart (including line items) by its GUID. Used by the
 * inactive-cart detail page.
 */
const getCartByGuid = async (
    cartGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    signal?: AbortSignal
): Promise<GetCartByGuidResponse> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartGetByGuid,
        params: { cartGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Poll the lightweight status of a cart — used while a cart is PENDING to
 * detect when the backend has finished processing it.
 */
const getCartStatus = async (
    cartGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel,
    signal?: AbortSignal
): Promise<IOnGetCartStatusDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartGetStatus,
        params: { cartGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "GET",
        headers,
        signal,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Create a fresh Stripe payment intent for an existing (typically PAYMENT_ERRORED
 * or PAYMENT_PENDING) cart so the user can retry checkout.
 */
const retryCartPayment = async (
    cartGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnCreateCartPaymentIntentDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartRetryPayment,
        params: { cartGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Tell the backend to confirm a payment intent for the cart (used after the
 * client successfully completes the Stripe payment sheet, when no redirect
 * flow is in play).
 */
const confirmCartPayment = async (
    cartGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnConfirmCartPaymentDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartConfirmPayment,
        params: { cartGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Cancel an inactive cart.
 */
const cancelCart = async (
    cartGuid: string,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnCancelCartDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartCancel,
        params: { cartGuid },
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

/**
 * Copy a subset of items from one cart (typically an inactive cart) into the
 * user's active draft cart.
 */
const copyCartItems = async (
    options: ICopyCartItemsRestDTO,
    userModel: IUserUI,
    supplierModel: ISupplierModel,
    vendorModel?: IVendorModel
): Promise<IOnCopyCartItemsDTO> => {
    const url = buildRestUrl({
        endpoint: RestEndpointsEnum.CartCopyItems,
    });

    const headers = buildAuthHeadersForFetch({
        userModel,
        supplierModel,
        vendorModel,
    });

    const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(options),
    });

    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || response.statusText);
    }

    return response.json();
};

const CartService = {
    createCartFromOrder,
    getCart,
    getInactiveCarts,
    getCartByGuid,
    getCartStatus,
    retryCartPayment,
    confirmCartPayment,
    cancelCart,
    copyCartItems,
};

export default CartService;
